import cn from 'clsx'
import { FC, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import type Player from 'video.js/dist/types/player'

import VideoPlayer from '@/components/VideoPlayer'
import { Button, Icon, Layout, Skeleton, Typography } from '@/components/ui'

import Copy from '@/assets/icons/copy.svg'

import {
	HighlightList,
	SharedHighlightControls,
	TranscriptionBlock
} from './components'
import {
	useHighlightFolderSelection,
	useHighlightSharing,
	useLive,
	useSharedHighlightsImport,
	useTranscriptionScrollSync
} from './hooks'

const SharedLive: FC = () => {
	const { token } = useParams()
	const navigate = useNavigate()

	const {
		sharedFolders,
		taskId,
		isSharedFoldersLoading,
		isSharedFoldersError,
		importSharedHighlights,
		isImportSharedHighlightsLoading
	} = useHighlightSharing(token)

	const { task, isTaskLoading, transcriptions, isTranscriptionsLoading } =
		useLive(taskId)

	const {
		containerRef,
		translatedRef,
		collectOriginalRef,
		collectTranslatedRef,
		handleScroll,
		scrollToSegment
	} = useTranscriptionScrollSync(transcriptions)

	const {
		isSelectMode,
		setIsSelectMode,
		selectedFolderIds,
		toggleFolderSelection,
		cancelFolderSelection,
		visibleFolders
	} = useHighlightFolderSelection(sharedFolders)

	const { importAllFolders, importSelectedFolders } = useSharedHighlightsImport(
		{
			taskId,
			selectedFolderIds,
			importSharedHighlights
		}
	)

	const [isCollapsed, setIsCollapsed] = useState(false)

	const playerRef = useRef<Player | null>(null)

	const isLoading =
		isSharedFoldersLoading || isTaskLoading || isTranscriptionsLoading

	const handleSeek = (seconds: number) => {
		if (playerRef.current) {
			playerRef.current.currentTime(seconds)
		}
	}

	if (isSharedFoldersError) {
		return (
			<Layout className='flex h-screen items-center justify-center'>
				<Typography variant='text-large'>
					Share link not found or expired
				</Typography>
			</Layout>
		)
	}

	return (
		<Layout className='relative flex h-screen w-full bg-neutral-900'>
			<div className='flex h-full w-full flex-col bg-white'>
				{/* Header */}
				<header className='flex h-[64px] shrink-0 items-center justify-between bg-black px-[40px] text-white'>
					<Typography variant='text' className='text-white'>
						You are viewing shared highlights
					</Typography>

					<Button
						className='rounded-[8px] bg-neutral-900 hover:bg-neutral-800 px-4 py-2'
						classNameText='flex items-center gap-3 text-white'
						disabled={!sharedFolders.length || isImportSharedHighlightsLoading}
						onClick={importAllFolders}
					>
						<Copy className='w-3 h-3 text-white' />
						<span>Save all folders to myself</span>
					</Button>
				</header>

				<div className='flex min-h-0 flex-1'>
					{/* Sidebar */}
					<aside
						className={cn(
							'border-r border-black/10 flex flex-col justify-between overflow-hidden relative transition-all',
							isCollapsed ? 'w-[32%]' : 'w-[25%]'
						)}
					>
						<button
							onClick={() => navigate('/home')}
							className='absolute z-50 top-2 left-2'
						>
							<Icon size={24} icon='arrow-back' color='#fff' />
						</button>

						<VideoPlayer
							src={task?.video?.src}
							type={task?.video?.type}
							isLoading={isLoading}
							onPlayerReady={player => {
								playerRef.current = player
							}}
						/>

						{!isLoading && (
							<>
								<SharedHighlightControls
									isSelectMode={isSelectMode}
									hasFolders={!!sharedFolders.length}
									hasSelectedFolders={!!selectedFolderIds.length}
									isImporting={isImportSharedHighlightsLoading}
									onSelectMode={() => setIsSelectMode(true)}
									onCancelSelection={cancelFolderSelection}
									onImportSelected={importSelectedFolders}
								/>

								<HighlightList
									folders={sharedFolders}
									deleteHighlight={() => {}}
									onOpenDeleteFolder={() => {}}
									updateFolder={() => {}}
									isUpdating={isImportSharedHighlightsLoading}
									isSelectMode={isSelectMode}
									selectedFolderIds={selectedFolderIds}
									onToggleFolder={toggleFolderSelection}
									moveSegmentToFolder={async () => {}}
									downloadFolder={() => {}}
									downloadSegment={() => {}}
									shareFolder={() => {}}
									isSharedView={true}
									onScrollToSegment={scrollToSegment}
								/>
							</>
						)}
					</aside>

					{/* Original transcription block */}
					<section
						className={cn(
							'flex-1 relative overflow-hidden',
							isCollapsed ? 'hidden' : 'flex-1'
						)}
					>
						{!isCollapsed && !isLoading && (
							<Button
								onClick={() => setIsCollapsed(true)}
								className='absolute top-5 right-4 z-10 bg-black/20 rounded-full w-fit p-2 hover:bg-black/15'
								icon={{
									name: 'back',
									size: 12,
									color: '#222222'
								}}
							/>
						)}

						<div
							className={cn(
								'transition-all ease-in-out flex flex-col overflow-y-auto h-full px-10 py-5 pt-10',
								isCollapsed ? 'px-0' : 'px-10'
							)}
							ref={containerRef}
						>
							{!isCollapsed && isLoading ? (
								<Skeleton className='mb-2 h-[26px]' />
							) : (
								<TranscriptionBlock
									items={transcriptions?.transcriptions?.original || []}
									folders={visibleFolders}
									refCollector={collectOriginalRef}
								/>
							)}
						</div>
					</section>

					{/* Translation block */}
					<section className='flex-1 relative bg-gray-100 overflow-hidden'>
						{isCollapsed && (
							<Button
								onClick={() => setIsCollapsed(false)}
								className='absolute top-5 left-4 z-10 bg-black/20 rounded-full w-fit p-2 rotate-180 hover:bg-black/15'
								icon={{
									name: 'back',
									size: 12,
									color: '#222222'
								}}
							/>
						)}

						<div
							ref={translatedRef}
							onScroll={handleScroll}
							className={cn(
								'transition-all ease-in-out flex flex-col overflow-y-auto h-full px-10 py-5 pt-10',
								isCollapsed ? 'items-center mx-auto' : 'w-auto'
							)}
						>
							{isLoading ? (
								<Skeleton className='mb-2 h-[26px]' />
							) : (
								<TranscriptionBlock
									isCollapsed={isCollapsed}
									items={transcriptions?.transcriptions?.translation || []}
									highlightable
									refCollector={collectTranslatedRef}
									onSeek={handleSeek}
									translation
									folders={visibleFolders}
									isTranscribingStopped={task?.status === 'stopped'}
									taskId={taskId}
								/>
							)}
						</div>
					</section>
				</div>
			</div>
		</Layout>
	)
}

export default SharedLive
